import React, { useMemo } from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import { OUTREACH_PITCH_KINDS, type OutreachPitchKind } from 'fym-shared/src/music24/outreach/dailyPitches';
import { TypedMemo } from '../../../common/types';
import type { CampaignPool } from '../hooks/useCampaignPool';
import { monthDay, poolDays } from './campaignFormat';
import { pitchKindGroupName } from './pitchKindCopy';
import { campaignPageMessages as messages } from './campaignPageMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the campaign page; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Campaign.module.scss';
/* eslint-enable css-modules/no-unused-class */

const DAY_MS = 24 * 60 * 60 * 1000;

interface FoundLastNightProps {
  /** `campaigns.poolSummary`; null while it loads. */
  pool: CampaignPool | null;
  rate: number | null;
  endsAt: string | null;
  /** The kinds of the campaign: the card lists only these (QA B06). Empty: every kind. */
  kinds: OutreachPitchKind[];
  /** True on the day of the start: no night passed, so the card shows every fit "so far" (QA B06). */
  soFar: boolean;
}

/** The lasting line: "3 days", "less than a day" or "past Dec 23"; null with no rate or no fit. */
function lastsText(intl: IntlShape, pool: CampaignPool, rate: number | null, endsAt: string | null): string | null {
  const days = poolDays(pool.total, rate ?? 0);
  if (days === null || pool.total === 0) {
    return null;
  }
  if (endsAt !== null && Date.now() + days * DAY_MS > Date.parse(endsAt)) {
    return intl.formatMessage(messages.lastsPast, { date: monthDay(intl, endsAt) });
  }
  return days === 0
    ? intl.formatMessage(messages.lastsLessThanDay)
    : intl.formatMessage(messages.lastsDays, { count: days });
}

/**
 * "Found last night" (design outreach-v2 g1 side): the people that the find of the night added, by
 * kind of the campaign, the good fits that wait, and how long they last at the campaign's rate. On
 * the day of the start no night passed: "Found so far" lists every fit of the campaign's kinds.
 */
const FoundLastNightComponent: React.FC<FoundLastNightProps> = ({ pool, rate, endsAt, kinds, soFar }) => {
  const intl = useIntl();
  const rows = useMemo(() => {
    const counts = soFar ? pool?.byKind : pool?.newByKind;
    return OUTREACH_PITCH_KINDS.filter(
      (kind) => (kinds.length === 0 || kinds.includes(kind)) && (counts?.[kind] ?? 0) > 0
    ).map((kind) => (
      <div key={kind} className={styles.kv}>
        <span>{pitchKindGroupName(intl, kind)}</span>
        <b>{intl.formatNumber(counts?.[kind] ?? 0)}</b>
      </div>
    ));
  }, [intl, kinds, pool, soFar]);
  if (pool === null) {
    return null;
  }
  const lasts = lastsText(intl, pool, rate, endsAt);

  return (
    <section className={styles.card} data-testid="found-last-night">
      <div className={styles.cardHead}>
        <h3 className={styles.cardTitle}>
          {intl.formatMessage(soFar ? messages.foundTitleSoFar : messages.foundTitle)}
        </h3>
        <span className={styles.meta}>
          {soFar
            ? intl.formatMessage(messages.foundMetaSoFar, { count: pool.total })
            : intl.formatMessage(messages.foundMeta, { count: pool.newTotal })}
        </span>
      </div>
      {rows.length === 0 ? (
        <p className={`${shared.small} m-0`}>
          {intl.formatMessage(soFar ? messages.foundNoneSoFar : messages.foundNone)}
        </p>
      ) : (
        <div className={styles.rows}>{rows}</div>
      )}
      <div className={styles.hair} />
      <div className={styles.rows}>
        <div className={styles.kv}>
          <span>{intl.formatMessage(messages.waiting)}</span>
          <b>{intl.formatNumber(pool.total)}</b>
        </div>
        {lasts !== null && rate !== null && (
          <div className={styles.kv}>
            <span>{intl.formatMessage(messages.lasts, { rate })}</span>
            <b>{lasts}</b>
          </div>
        )}
      </div>
    </section>
  );
};

export const FoundLastNight = TypedMemo(FoundLastNightComponent);
